import React, { Component } from 'react';
import { Warning as AlertTriangle, ArrowsClockwise as RefreshCw, House as Home } from '@phosphor-icons/react';

class ErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null, errorInfo: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    this.setState({ errorInfo });
    // Log error to console in development
    console.error('ErrorBoundary caught an error:', error, errorInfo);
  }

  handleRetry = () => {
    this.setState({ hasError: false, error: null, errorInfo: null });
    // Force reload the page to clear any cached bad state
    window.location.reload();
  };

  handleGoHome = () => {
    window.location.href = '/';
  };

  render() {
    if (this.state.hasError) {
      const isChunkError = this.state.error?.message?.includes('Loading chunk') ||
                           this.state.error?.message?.includes('Failed to fetch') ||
                           this.state.error?.name === 'ChunkLoadError';

      return (
        <div className="min-h-screen bg-primaryB-50 dark:bg-primaryB-900 flex items-center justify-center p-4">
          <div className="max-w-md w-full bg-primaryB-50 dark:bg-primaryB-800 rounded-2xl shadow-elevated p-8 text-center">
            <div className="w-16 h-16 mx-auto mb-6 rounded-full bg-red-100 dark:bg-red-900/30 flex items-center justify-center">
              <AlertTriangle weight="bold" className="w-8 h-8 text-red-500" />
            </div>

            <h1 className="text-xl font-bold text-primaryB-900 dark:text-white mb-2">
              {isChunkError ? 'Page Failed to Load' : 'Something went wrong'}
            </h1>

            <p className="text-primaryB-500 dark:text-primaryB-400 text-sm mb-6">
              {isChunkError
                ? 'This page could not be loaded. This usually happens when the app was updated. Please refresh to get the latest version.'
                : 'An unexpected error occurred. Please try again or return to the home page.'}
            </p>

            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <button
                onClick={this.handleRetry}
                className="inline-flex items-center justify-center gap-2 px-6 py-3 bg-primaryB-800 dark:bg-primaryB-700 text-white rounded-xl font-bold text-sm hover:bg-primaryB-700 dark:hover:bg-primaryB-600 transition-colors"
              >
                <RefreshCw weight="bold" className="w-4 h-4" />
                Refresh Page
              </button>

              <button
                onClick={this.handleGoHome}
                className="inline-flex items-center justify-center gap-2 px-6 py-3 border border-primaryB-300 dark:border-primaryB-600 text-primaryB-700 dark:text-primaryB-300 rounded-xl font-bold text-sm hover:bg-primaryB-50 dark:hover:bg-primaryB-700 transition-colors"
              >
                <Home weight="bold" className="w-4 h-4" />
                Go Home
              </button>
            </div>

            {process.env.NODE_ENV === 'development' && this.state.error && (
              <details className="mt-6 text-left">
                <summary className="text-xs text-primaryB-400 cursor-pointer hover:text-primaryB-600">
                  Technical Details
                </summary>
                <pre className="mt-2 p-3 bg-primaryB-100 dark:bg-primaryB-900 rounded-xl text-xs text-red-600 dark:text-red-400 overflow-auto max-h-40">
                  {this.state.error.toString()}
                  {this.state.errorInfo?.componentStack}
                </pre>
              </details>
            )}
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
